import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '../hooks/useFetch';
import { getIconComponent } from '../utils/iconMapper';
import api from '../services/api';
import PageLoader from '../components/PageLoader';
import {
    Heart,
    Smartphone,
    RefreshCw,
    CheckCircle,
    Users,
    MessageCircle,
    Zap,
    TrendingUp,
    Globe,
    DollarSign,
    ArrowRight,
    Send,
    Shield,
    Clock,
    Award,
    ChevronRight,
    Play,
    Star,
    Quote,
    Mail,
    Phone,
    MapPin,
    Sparkles,
    Crown,
    BarChart3,
    UserCheck,
    MessageSquare,
    Bell,
    Calendar,
    Gift,
    LayoutGrid,
    Target,
    Award as AwardIcon,
    X,
    Check,
    Loader,
} from 'lucide-react';

// ===== REQUEST DEMO MODAL COMPONENT =====
const RequestDemoModal = ({ isOpen, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        churchName: '',
        churchSize: '50-100',
        role: 'Pastor',
        message: '',
        agreeToTerms: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
    const [errors, setErrors] = useState({});
    const modalRef = useRef(null);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    // Handle escape key
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
        }

        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }
        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^[0-9+\-\s()]{8,15}$/.test(formData.phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }
        if (!formData.churchName.trim()) newErrors.churchName = 'Church name is required';
        if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Call the actual API
            const response = await api.post('/demo-request', formData);
            // Check response status (expected 201)
            if (response.data.status === 201) {
                setSubmitStatus('success');
                onSubmit(formData);
                // Reset form after 3 seconds
                setTimeout(() => {
                    setFormData({
                        fullName: '',
                        email: '',
                        phone: '',
                        churchName: '',
                        churchSize: '50-100',
                        role: 'Pastor',
                        message: '',
                        agreeToTerms: false,
                    });
                    setSubmitStatus(null);
                    onClose();
                }, 3000);
            } else {
                throw new Error(response.data.message || 'Submission failed');
            }
        } catch (error) {
            setSubmitStatus('error');
            // Show error message from backend if available
            const msg = error.response?.data?.message || error.message || 'Something went wrong';
            // Optionally set error in state to display
            setTimeout(() => setSubmitStatus(null), 3000);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="demo-modal-overlay">
            <div className="demo-modal" ref={modalRef}>
                {/* Modal Header */}
                <div className="demo-modal-header">
                    <div className="demo-modal-header-content">
                        <div className="demo-modal-icon">
                            <Calendar size={24} />
                        </div>
                        <div>
                            <h3 className="demo-modal-title">Request a Demo</h3>
                            <p className="demo-modal-subtitle">
                                See how FaithConnect can transform your church's member care
                            </p>
                        </div>
                    </div>
                    <button className="demo-modal-close" onClick={onClose}>
                        <X size={24} />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="demo-modal-body">
                    {submitStatus === 'success' ? (
                        <div className="demo-modal-success">
                            <div className="demo-modal-success-icon">
                                <Check size={48} />
                            </div>
                            <h4>Demo Request Sent! 🎉</h4>
                            <p>
                                Thank you for your interest in FaithConnect. Our team will reach out
                                within 24 hours to schedule your personalized demo.
                            </p>
                            <div className="demo-modal-success-details">
                                <div className="demo-modal-success-item">
                                    <Mail size={16} />
                                    <span>Check your email for confirmation</span>
                                </div>
                                <div className="demo-modal-success-item">
                                    <Phone size={16} />
                                    <span>We'll call you at {formData.phone}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="demo-modal-form">
                            {/* Full Name & Email Row */}
                            <div className="demo-form-row">
                                <div className="demo-form-group">
                                    <label htmlFor="fullName" className="demo-form-label">
                                        Full Name <span className="demo-form-required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="fullName"
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                        className={`demo-form-input ${errors.fullName ? 'error' : ''}`}
                                        placeholder="e.g., Pastor John Doe"
                                        disabled={isSubmitting}
                                    />
                                    {errors.fullName && (
                                        <span className="demo-form-error">{errors.fullName}</span>
                                    )}
                                </div>

                                <div className="demo-form-group">
                                    <label htmlFor="email" className="demo-form-label">
                                        Email <span className="demo-form-required">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={`demo-form-input ${errors.email ? 'error' : ''}`}
                                        placeholder="pastor@church.org"
                                        disabled={isSubmitting}
                                    />
                                    {errors.email && (
                                        <span className="demo-form-error">{errors.email}</span>
                                    )}
                                </div>
                            </div>

                            {/* Phone & Church Name Row */}
                            <div className="demo-form-row">
                                <div className="demo-form-group">
                                    <label htmlFor="phone" className="demo-form-label">
                                        Phone Number <span className="demo-form-required">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className={`demo-form-input ${errors.phone ? 'error' : ''}`}
                                        placeholder="+233 20 000 0000"
                                        disabled={isSubmitting}
                                    />
                                    {errors.phone && (
                                        <span className="demo-form-error">{errors.phone}</span>
                                    )}
                                </div>

                                <div className="demo-form-group">
                                    <label htmlFor="churchName" className="demo-form-label">
                                        Church Name <span className="demo-form-required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="churchName"
                                        name="churchName"
                                        value={formData.churchName}
                                        onChange={handleChange}
                                        className={`demo-form-input ${errors.churchName ? 'error' : ''}`}
                                        placeholder="e.g., Grace Chapel International"
                                        disabled={isSubmitting}
                                    />
                                    {errors.churchName && (
                                        <span className="demo-form-error">{errors.churchName}</span>
                                    )}
                                </div>
                            </div>

                            {/* Church Size & Role Row */}
                            <div className="demo-form-row">
                                <div className="demo-form-group">
                                    <label htmlFor="churchSize" className="demo-form-label">
                                        Church Size
                                    </label>
                                    <select
                                        id="churchSize"
                                        name="churchSize"
                                        value={formData.churchSize}
                                        onChange={handleChange}
                                        className="demo-form-select"
                                        disabled={isSubmitting}
                                    >
                                        <option value="0-50">0 - 50 members</option>
                                        <option value="50-100">50 - 100 members</option>
                                        <option value="100-250">100 - 250 members</option>
                                        <option value="250-500">250 - 500 members</option>
                                        <option value="500-1000">500 - 1,000 members</option>
                                        <option value="1000+">1,000+ members</option>
                                    </select>
                                </div>

                                <div className="demo-form-group">
                                    <label htmlFor="role" className="demo-form-label">
                                        Your Role
                                    </label>
                                    <select
                                        id="role"
                                        name="role"
                                        value={formData.role}
                                        onChange={handleChange}
                                        className="demo-form-select"
                                        disabled={isSubmitting}
                                    >
                                        <option value="Pastor">Pastor</option>
                                        <option value="Associate Pastor">Associate Pastor</option>
                                        <option value="Church Administrator">Church Administrator</option>
                                        <option value="Ministry Leader">Ministry Leader</option>
                                        <option value="Volunteer">Volunteer</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>

                            {/* Message */}
                            <div className="demo-form-group">
                                <label htmlFor="message" className="demo-form-label">
                                    Message (Optional)
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    className="demo-form-textarea"
                                    placeholder="Tell us about your church and what you're looking for..."
                                    rows="3"
                                    disabled={isSubmitting}
                                />
                            </div>

                            {/* Terms */}
                            <div className="demo-form-checkbox-group">
                                <label className="demo-form-checkbox">
                                    <input
                                        type="checkbox"
                                        name="agreeToTerms"
                                        checked={formData.agreeToTerms}
                                        onChange={handleChange}
                                        disabled={isSubmitting}
                                    />
                                    <span className="demo-form-checkbox-custom">
                                        {formData.agreeToTerms && <Check size={14} />}
                                    </span>
                                    <span className="demo-form-checkbox-label">
                                        I agree to be contacted about this demo and understand my
                                        data will be handled according to the{' '}
                                        <Link to="/privacy" className="demo-form-link">
                                            Privacy Policy
                                        </Link>
                                    </span>
                                </label>
                                {errors.agreeToTerms && (
                                    <span className="demo-form-error">{errors.agreeToTerms}</span>
                                )}
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                className={`demo-form-submit ${isSubmitting ? 'submitting' : ''}`}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader size={20} className="demo-form-spinner" />
                                        <span>Submitting...</span>
                                    </>
                                ) : (
                                    <>
                                        <Send size={18} />
                                        <span>Request Demo</span>
                                    </>
                                )}
                            </button>

                            {submitStatus === 'error' && (
                                <div className="demo-form-error-message">
                                    Something went wrong. Please try again.
                                </div>
                            )}
                        </form>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="demo-modal-footer">
                    <div className="demo-modal-footer-info">
                        <Clock size={14} />
                        <span>Response within 24 hours</span>
                    </div>
                    <div className="demo-modal-footer-info">
                        <Shield size={14} />
                        <span>Your data is secure</span>
                    </div>
                    <div className="demo-modal-footer-info">
                        <Check size={14} />
                        <span>No obligation</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ===== ADVANCED ANIMATION HOOKS =====
const useStaggerAnimation = (items, delay = 100) => {
    const [visibleItems, setVisibleItems] = useState([]);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    items.forEach((_, index) => {
                        setTimeout(() => {
                            setVisibleItems((prev) => [...prev, index]);
                        }, index * delay);
                    });
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, [items, delay]);

    return { ref, visibleItems };
};

// ===== COUNT-UP HOOK =====
const useCountUp = (end, duration = 2000, startDelay = 0, suffix = '') => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const [hasAnimated, setHasAnimated] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated) {
                    setHasAnimated(true);
                    const startTime = performance.now() + startDelay;
                    const startValue = 0;
                    const range = end;

                    const step = (currentTime) => {
                        if (currentTime < startTime) {
                            requestAnimationFrame(step);
                            return;
                        }

                        const progress = Math.min((currentTime - startTime) / duration, 1);
                        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
                        const val = Math.floor(startValue + range * easeOutCubic);
                        setCount(val);

                        if (progress < 1) {
                            requestAnimationFrame(step);
                        }
                    };
                    requestAnimationFrame(step);
                }
            },
            { threshold: 0.3 }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [end, duration, startDelay, hasAnimated]);

    return { ref, count: count + suffix };
};

// ===== PREMIUM FEATURE CARD =====
const PremiumFeatureCard = ({ icon: Icon, title, description, color, index, isVisible }) => (
    <div
        className={`premium-feature-card ${isVisible ? 'visible' : ''}`}
        style={{ transitionDelay: `${index * 80}ms` }}
    >
        <div className="premium-feature-icon" style={{ background: color.bg, color: color.text }}>
            <Icon size={24} />
        </div>
        <div className="premium-feature-content">
            <h3>{title}</h3>
            <p>{description}</p>
            <Link to="/features" className="premium-feature-link">
                Learn more <ChevronRight size={14} />
            </Link>
        </div>
    </div>
);

// ===== TESTIMONIAL ITEM =====
const TestimonialItem = ({ quote, author, role, church, rating, image }) => (
    <div className="testimonial-item">
        <div className="testimonial-rating">
            {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} fill={i < rating ? '#C9A84B' : 'none'} color="#C9A84B" />
            ))}
        </div>
        <p className="testimonial-quote">"{quote}"</p>
        <div className="testimonial-author-info">
            {image ? (
                <img src={image} alt={author} className="testimonial-avatar" />
            ) : (
                <div className="testimonial-avatar-placeholder">{author.charAt(0)}</div>
            )}
            <div>
                <div className="testimonial-author-name">{author}</div>
                <div className="testimonial-author-role">{role}, {church}</div>
            </div>
        </div>
    </div>
);

// ===== MAIN HOME COMPONENT =====
function Home() {
    // State
    const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [hoveredStat, setHoveredStat] = useState(null);

    // Fetch data from APIs
    const { data: featuresData, loading: featuresLoading } = useFetch('/features');
    const { data: testimonialsData, loading: testimonialsLoading } = useFetch('/testimonials');
    const { data: statsData, loading: statsLoading } = useFetch('/stats');
    const { data: regionsData, loading: regionsLoading } = useFetch('/regions');
    const { data: pricingData, loading: pricingLoading } = useFetch('/pricing');
    const { data: settingsData, loading: settingsLoading } = useFetch('/settings?group=home');

    // Transform features: map icon strings to components
    const features = useMemo(() => {
        if (!featuresData) return [];
        return featuresData.map((f) => ({
            ...f,
            icon: getIconComponent(f.icon),
        }));
    }, [featuresData]);

    // Transform testimonials
    const testimonials = useMemo(() => {
        if (!testimonialsData) return [];
        return testimonialsData.map((t) => ({ ...t, image: t.image || null }));
    }, [testimonialsData]);

    // Stats, regions, pricing are already arrays
    const stats = useMemo(() => statsData || [], [statsData]);
    const regions = useMemo(() => regionsData || [], [regionsData]);
    const pricingPlans = useMemo(() => pricingData || [], [pricingData]);

    // Social proof logos from settings (JSON string)
    const socialProofLogos = useMemo(() => {
        if (!settingsData) return [];
        try {
            return JSON.parse(settingsData.social_proof_logos || '[]');
        } catch {
            return [];
        }
    }, [settingsData]);

    // Count-up values (static for now; could be from settings)
    const { ref: n1Ref, count: n1 } = useCountUp(1247, 2000, 300);
    const { ref: n2Ref, count: n2 } = useCountUp(892, 2000, 500);
    const { ref: n3Ref, count: n3 } = useCountUp(934, 2000, 700);

    // Features stagger animation
    const { ref: featuresRef, visibleItems: visibleFeatures } = useStaggerAnimation(features, 100);

    // Handle demo submission
    const handleDemoSubmit = useCallback((data) => {
        console.log('Demo request submitted:', data);
        // The actual API call is handled inside the modal
    }, []);

    // Loading state with PageLoader
    if (featuresLoading || testimonialsLoading || statsLoading || regionsLoading || pricingLoading || settingsLoading) {
        return <PageLoader message="Loading your experience..." />;
    }

    return (
        <>
            {/* ===== HERO SECTION ===== */}
            <section className="hero-premium">
                {/* Animated gradient background */}
                <div className="hero-premium-bg">
                    <div className="hero-premium-gradient-1" />
                    <div className="hero-premium-gradient-2" />
                    <div className="hero-premium-gradient-3" />
                </div>

                {/* Floating particles */}
                <div className="hero-premium-particles">
                    {[...Array(12)].map((_, i) => (
                        <div
                            key={i}
                            className="hero-premium-particle"
                            style={{
                                '--delay': `${i * 0.4}s`,
                                '--size': `${2 + Math.random() * 4}px`,
                                '--x': `${Math.random() * 100}%`,
                                '--y': `${Math.random() * 100}%`,
                            }}
                        />
                    ))}
                </div>

                <div className="wrap hero-premium-grid">
                    <div className="hero-premium-content">
                        <div className="hero-premium-badge">
                            <Sparkles size={14} />
                            <span>Church member care software</span>
                        </div>

                        <h2 className="hero-premium-title">
                            No one unnoticed.
                            <span className="hero-premium-highlight"> No one forgotten.</span>
                        </h2>

                        <p className="hero-premium-sub">
                            FaithConnect helps Ghanaian churches track attendance, automate follow-up,
                            and send SMS — so every member feels seen and cared for.
                        </p>

                        <div className="hero-premium-actions">
                            <Link to="/signup" className="btn-premium-primary">
                                Start free trial
                                <ArrowRight size={18} className="btn-premium-icon" />
                            </Link>
                            <button
                                className="btn-premium-secondary"
                                onClick={() => setIsDemoModalOpen(true)}
                            >
                                <Play size={18} />
                                Request Demo
                            </button>
                        </div>

                        <div className="hero-premium-trust">
                            <div className="hero-premium-trust-item">
                                <div className="hero-premium-trust-dot" />
                                <span>Works on any phone</span>
                            </div>
                            <div className="hero-premium-trust-item">
                                <div className="hero-premium-trust-dot" />
                                <span>Trusted by 60+ churches</span>
                            </div>
                            <div className="hero-premium-trust-item">
                                <div className="hero-premium-trust-dot" />
                                <span>Free 14-day trial</span>
                            </div>
                        </div>
                    </div>

                    {/* Hero Dashboard */}
                    <div className="hero-premium-dashboard">
                        <div className="premium-glass-card">
                            <div className="premium-glass-header">
                                <div className="premium-glass-status">
                                    <span className="premium-glass-dot" />
                                    <span className="premium-glass-title">Grace Chapel Int'l</span>
                                </div>
                                <div className="premium-glass-badge">Live</div>
                            </div>

                            <div className="premium-glass-stats">
                                <div className="premium-glass-stat">
                                    <div className="premium-glass-stat-label">Members</div>
                                    <div className="premium-glass-stat-number" ref={n1Ref}>
                                        {n1}
                                    </div>
                                    <div className="premium-glass-stat-bar">
                                        <div
                                            className="premium-glass-stat-fill"
                                            style={{ width: '78%' }}
                                        />
                                    </div>
                                </div>
                                <div className="premium-glass-stat">
                                    <div className="premium-glass-stat-label">SMS Sent</div>
                                    <div className="premium-glass-stat-number" ref={n2Ref}>
                                        {n2}
                                    </div>
                                    <div className="premium-glass-stat-bar">
                                        <div
                                            className="premium-glass-stat-fill"
                                            style={{ width: '94%' }}
                                        />
                                    </div>
                                </div>
                                <div className="premium-glass-stat">
                                    <div className="premium-glass-stat-label">Attendance</div>
                                    <div className="premium-glass-stat-number" ref={n3Ref}>
                                        {n3}
                                    </div>
                                    <div className="premium-glass-stat-bar">
                                        <div
                                            className="premium-glass-stat-fill"
                                            style={{ width: '75%' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="premium-glass-members">
                                <div className="premium-glass-member">
                                    <div
                                        className="premium-glass-avatar"
                                        style={{ background: '#6C2BD1' }}
                                    >
                                        KO
                                    </div>
                                    <div className="premium-glass-member-info">
                                        <div className="premium-glass-member-name">
                                            Kwame Owusu
                                        </div>
                                        <div className="premium-glass-member-status">
                                            Absent 2 weeks
                                        </div>
                                    </div>
                                    <div className="premium-glass-member-action">Reach out</div>
                                </div>
                                <div className="premium-glass-member">
                                    <div
                                        className="premium-glass-avatar"
                                        style={{ background: '#307A4E' }}
                                    >
                                        EF
                                    </div>
                                    <div className="premium-glass-member-info">
                                        <div className="premium-glass-member-name">
                                            Efua Frimpong
                                        </div>
                                        <div className="premium-glass-member-status">
                                            Present today
                                        </div>
                                    </div>
                                    <div
                                        className="premium-glass-badge"
                                        style={{ background: '#307A4E' }}
                                    >
                                        Here
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="hero-premium-scroll">
                    <span>Scroll to explore</span>
                    <div className="hero-premium-scroll-line" />
                </div>
            </section>

            {/* ===== SOCIAL PROOF ===== */}
            <section className="social-proof-section">
                <div className="wrap">
                    <div className="social-proof-content">
                        <span className="social-proof-label">Trusted by church leaders at</span>
                        <div className="social-proof-logos">
                            {socialProofLogos.map((name, i) => (
                                <span key={i} className="social-proof-logo">
                                    {name}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== PROBLEM/SOLUTION (static) ===== */}
            <section className="problem-solution-section">
                <div className="wrap">
                    <div className="problem-solution-grid">
                        <div className="problem-solution-content">
                            <div className="eyebrow">The challenge</div>
                            <h2>Past 100 members, memory isn't enough.</h2>
                            <p className="problem-solution-lead">
                                Growing churches face the same three challenges. FaithConnect solves
                                all of them.
                            </p>

                            <div className="problem-solution-items">
                                <div className="problem-solution-item">
                                    <div className="problem-solution-number">01</div>
                                    <div>
                                        <strong>Attendance lives in someone's head</strong>
                                        <p>Or a notebook. Or a spreadsheet nobody updates.</p>
                                        <div className="problem-solution-check">
                                            ✓ Track from any phone
                                        </div>
                                    </div>
                                </div>
                                <div className="problem-solution-item">
                                    <div className="problem-solution-number">02</div>
                                    <div>
                                        <strong>Follow-up depends on memory</strong>
                                        <p>Three Sundays missed and nobody notices.</p>
                                        <div className="problem-solution-check">
                                            ✓ Automated absence alerts
                                        </div>
                                    </div>
                                </div>
                                <div className="problem-solution-item">
                                    <div className="problem-solution-number">03</div>
                                    <div>
                                        <strong>Communication is one-size-fits-all</strong>
                                        <p>Everyone gets the same announcement.</p>
                                        <div className="problem-solution-check">
                                            ✓ Personalized bulk SMS
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="problem-solution-quote">
                            <Quote size={32} className="problem-solution-quote-icon" />
                            <p>
                                "Before FaithConnect, we didn't know Kwame had missed a month until
                                he told us he'd started attending elsewhere."
                            </p>
                            <div className="problem-solution-quote-author">
                                <div className="problem-solution-quote-avatar">PK</div>
                                <div>
                                    <div className="problem-solution-quote-name">Pastor Kwame</div>
                                    <div className="problem-solution-quote-role">
                                        Member Care Lead, Accra
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== FEATURES (dynamic) ===== */}
            <section className="premium-features-section" ref={featuresRef}>
                <div className="wrap">
                    <div className="premium-features-header">
                        <div className="eyebrow">Features</div>
                        <h2>
                            Everything you need to{' '}
                            <span className="highlight">care for every member.</span>
                        </h2>
                        <p>
                            Built for how Ghanaian churches actually run. Simple, reliable, and
                            effective.
                        </p>
                    </div>

                    <div className="premium-features-grid">
                        {features.map((feature, index) => (
                            <PremiumFeatureCard
                                key={index}
                                icon={feature.icon}
                                title={feature.title}
                                description={feature.description}
                                color={feature.color}
                                index={index}
                                isVisible={visibleFeatures.includes(index)}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== STATS (dynamic) ===== */}
            <section className="premium-stats-section">
                <div className="wrap">
                    <div className="premium-stats-grid">
                        {stats.map((stat, idx) => (
                            <div
                                key={idx}
                                className="premium-stat-card"
                                onMouseEnter={() => setHoveredStat(idx)}
                                onMouseLeave={() => setHoveredStat(null)}
                            >
                                <div className="premium-stat-number">{stat.value}</div>
                                <div className="premium-stat-label">{stat.label}</div>
                                {stat.trend && <div className="premium-stat-trend">{stat.trend}</div>}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== TESTIMONIALS (dynamic) ===== */}
            <section className="premium-testimonials-section">
                <div className="wrap">
                    <div className="premium-testimonials-header">
                        <div className="eyebrow">Testimonials</div>
                        <h2>
                            Loved by church leaders <span className="highlight">across Ghana.</span>
                        </h2>
                    </div>

                    <div className="premium-testimonials-grid">
                        {testimonials.map((testimonial, i) => (
                            <TestimonialItem key={i} {...testimonial} />
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== REGION (dynamic) ===== */}
            <section className="premium-region-section">
                <div className="wrap">
                    <div className="premium-region-grid">
                        <div className="premium-region-content">
                            <div className="eyebrow light">Where we work</div>
                            <h2>
                                Rooted in Ghana. <span className="highlight">Built for Africa.</span>
                            </h2>
                            <p className="premium-region-lead">
                                Designed around real conditions — shared phones, patchy data, and SMS
                                as the most reliable line to every member.
                            </p>

                            <div className="premium-region-points">
                                <div className="premium-region-point">
                                    <div className="premium-region-point-icon">
                                        <Smartphone size={18} />
                                    </div>
                                    <div>
                                        <strong>SMS-first, not app-first</strong>
                                        <p>
                                            Every feature works over SMS. No smartphone or data
                                            required.
                                        </p>
                                    </div>
                                </div>
                                <div className="premium-region-point">
                                    <div className="premium-region-point-icon">
                                        <DollarSign size={18} />
                                    </div>
                                    <div>
                                        <strong>Local payment, local support</strong>
                                        <p>
                                            Mobile money billing and support that understands
                                            Ghanaian churches.
                                        </p>
                                    </div>
                                </div>
                                <div className="premium-region-point">
                                    <div className="premium-region-point-icon">
                                        <Globe size={18} />
                                    </div>
                                    <div>
                                        <strong>Built to expand</strong>
                                        <p>Ready for Nigeria, Kenya, and beyond.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="premium-region-map">
                            <div className="premium-map-card">
                                <div className="premium-map-title">Availability</div>
                                <div className="premium-map-countries">
                                    {regions.map((region, idx) => (
                                        <div key={idx} className="premium-map-country">
                                            <span className="premium-map-flag">{region.flag}</span>
                                            <span className="premium-map-name">{region.name}</span>
                                            <span className={`premium-map-status ${region.status}`}>
                                                {region.status === 'live'
                                                    ? 'Live'
                                                    : region.status === 'soon'
                                                    ? 'Soon'
                                                    : region.status}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== PRICING (dynamic teaser) ===== */}
            <section className="premium-pricing-section">
                <div className="wrap">
                    <div className="premium-pricing-content">
                        <div className="eyebrow">Pricing</div>
                        <h2>
                            Priced for <span className="highlight">churches of every size.</span>
                        </h2>
                        <p>From small fellowships to multi-branch cathedrals.</p>

                        {pricingPlans.length > 0 && (
                            <div className="premium-pricing-card">
                                <div className="premium-pricing-card-content">
                                    <div className="premium-pricing-price">
                                        <span className="premium-pricing-currency">
                                            {pricingPlans[0].currency}
                                        </span>
                                        <span className="premium-pricing-amount">
                                            {pricingPlans[0].price}
                                        </span>
                                        <span className="premium-pricing-period">
                                            {pricingPlans[0].period}
                                        </span>
                                    </div>
                                    <div className="premium-pricing-features">
                                        {pricingPlans[0].features.map((feat, idx) => (
                                            <span key={idx}>✓ {feat}</span>
                                        ))}
                                    </div>
                                    <Link
                                        to={pricingPlans[0].cta_link || '/pricing'}
                                        className="btn-premium-primary full-width"
                                    >
                                        {pricingPlans[0].cta_text || 'View all plans'}
                                        <ArrowRight size={18} />
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* ===== FINAL CTA (static) ===== */}
            <section className="premium-cta-section">
                <div className="wrap">
                    <div className="premium-cta-content">
                        <div className="premium-cta-badge">14-day free trial</div>
                        <h2>
                            Your congregation deserves to be{' '}
                            <span className="highlight">known by name.</span>
                        </h2>
                        <p>
                            Join 60+ churches already using FaithConnect to care for their members.
                        </p>

                        <div className="premium-cta-actions">
                            <Link to="/signup" className="btn-premium-primary large">
                                Start free trial
                                <ArrowRight size={20} className="btn-premium-icon" />
                            </Link>
                            <button
                                className="btn-premium-secondary large"
                                onClick={() => setIsDemoModalOpen(true)}
                            >
                                <Play size={18} />
                                Request demo
                            </button>
                        </div>

                        <div className="premium-cta-perks">
                            <span>✓ No credit card required</span>
                            <span>✓ 14-day free trial</span>
                            <span>✓ Cancel anytime</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== REQUEST DEMO MODAL ===== */}
            <RequestDemoModal
                isOpen={isDemoModalOpen}
                onClose={() => setIsDemoModalOpen(false)}
                onSubmit={handleDemoSubmit}
            />
        </>
    );
}

export default Home;